import { useCallback, useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { CheckCircle2, ExternalLink, Flag, Loader2, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";

import { publishBadge } from "@/braining/badge-count";
import {
  loadComplaint,
  loadComplaints,
  resendComplaint,
  setComplaintResolved,
  type ComplaintDetails,
  type ComplaintsView,
} from "@/braining/complaints.functions";
import type { ComplaintRow } from "@/braining/complaints-store";
import { counted } from "@/braining/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/**
 * Жалобы: что сказали менеджеры и что с этим стало.
 *
 * Прогон 21.09 с Викой и Леной дал девять жалоб за полчаса — и показал, что
 * читать их негде. Задача в GitHub несёт указатель (заказчик и суммы наружу
 * не уходят, решение 19.09), а всё остальное лежало в базе так, что добраться
 * до него можно было только через psql: снимок экрана — байтами в колонке.
 *
 * Здесь показывается всё: это база агентства и её собственные роли. Граница
 * «что не уходит» проходит по внешним каналам, а не внутри системы.
 *
 * Разбор — отметка, а не удаление: жалоба остаётся следом ошибки, и по
 * отметке видно, что уже посмотрели, а что ждёт.
 */
export const Route = createFileRoute("/complaints")({
  beforeLoad: ({ context }) => {
    // Разбор работы системы — дело руководителя и админа. Сервер проверяет то
    // же самое (requireAdminOrRop), а в базе — RLS: менеджер, дошедший сюда
    // руками, увидит только свои жалобы.
    const role = context.user?.role;
    if (role !== "admin" && role !== "rop") throw redirect({ to: "/" });
  },
  component: ComplaintsPage,
});

const KIND: Record<string, string> = {
  assistant: "ассистент",
  text: "текст",
  invariant: "инвариант",
};

function ComplaintsPage() {
  const [view, setView] = useState<ComplaintsView | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const reload = useCallback(() => {
    loadComplaints()
      .then((v) => {
        setView(v);
        // Панель слушает: цифра рядом с «Жалобами» — это то, что здесь видно,
        // а не то, что было на входе в систему.
        publishBadge("complaints", v.rows.filter((r) => !r.resolvedAt).length);
      })
      .catch((e: unknown) => {
        console.error("[complaints] не удалось прочитать список", e);
        toast.error("Не удалось прочитать жалобы");
      });
  }, []);

  useEffect(reload, [reload]);

  const rows = view?.rows ?? [];
  const open = rows.filter((r) => !r.resolvedAt);
  const done = rows.filter((r) => r.resolvedAt);
  const shown = showResolved ? done : open;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Жалобы</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Всё, что менеджеры отправили кнопкой «Сообщить об ошибке», и нарушения, которые система
          нашла сама при записи калькулятора. Разработчику продукта уходит указатель — номер, экран
          и слова; заказчик, суммы и снимок остаются здесь. Закрытая у него задача помечает жалобу
          разобранной сама — сверяется при каждом открытии этого экрана.
        </p>
      </div>

      {/* Сверка происходит до того, как экран нарисован, и без этой строки
          отметка «разобрано» появлялась бы сама собой, без объяснения. */}
      {view && view.closedNow > 0 && (
        <Card className="border-success/40 bg-success/5">
          <CardContent className="py-3 text-sm">
            <p>
              {counted(view.closedNow, "жалоба", "жалобы", "жалоб")}{" "}
              {view.closedNow === 1 ? "помечена" : "помечены"} разобранными: задачи закрыты у
              разработчика продукта.
            </p>
          </CardContent>
        </Card>
      )}

      {view && view.unreported > 0 && (
        <Card className="border-warning/40 bg-warning/5">
          <CardContent className="py-3 text-sm">
            <p className="font-medium">
              {counted(view.unreported, "жалоба", "жалобы", "жалоб")} записано, но задача не
              завелась.
            </p>
            <p className="text-muted-foreground">
              Канал указателей молчит: проверьте токен GitHub. Каждую можно дослать кнопкой
              «Дослать».
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={showResolved ? "outline" : "default"}
          onClick={() => setShowResolved(false)}
        >
          Ждут разбора {open.length > 0 && <Badge variant="secondary">{open.length}</Badge>}
        </Button>
        <Button
          size="sm"
          variant={showResolved ? "default" : "outline"}
          onClick={() => setShowResolved(true)}
        >
          Разобранные {done.length > 0 && <Badge variant="secondary">{done.length}</Badge>}
        </Button>
      </div>

      {view === null ? (
        <p className="text-sm text-muted-foreground">Читаем…</p>
      ) : shown.length === 0 ? (
        <p className="max-w-2xl text-sm text-muted-foreground">
          {showResolved
            ? "Разобранных пока нет."
            : "Ничего не ждёт разбора. Жалоба появляется здесь, как только её отправили с любого экрана."}
        </p>
      ) : (
        <div className="grid gap-3">
          {shown.map((row) => (
            <ComplaintCard key={row.id} row={row} onChanged={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function ComplaintCard({ row, onChanged }: { row: ComplaintRow; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState<ComplaintDetails | null>(null);
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);

  // Подробности и снимок — только когда жалобу открыли: снимок «Расчёта»
  // весит под мегабайт, и грузить их все разом значило бы ждать полминуты
  // ради списка.
  useEffect(() => {
    if (!open || details) return;
    loadComplaint({ data: { id: row.id } })
      .then((d) => setDetails(d))
      .catch((e: unknown) => console.error("[complaints] не удалось прочитать жалобу", e));
  }, [open, details, row.id]);

  async function resolve(resolved: boolean) {
    setBusy(true);
    try {
      await setComplaintResolved({
        data: { id: row.id, resolved, resolution: resolution.trim() || undefined },
      });
      toast.success(resolved ? `Жалоба №${row.id} разобрана` : `Жалоба №${row.id} снова в работе`);
      onChanged();
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    try {
      const res = await resendComplaint({ data: { id: row.id } });
      if (res.ok) {
        toast.success("Задача заведена");
        onChanged();
      } else toast.error("Задача не завелась", { description: res.error });
    } catch {
      toast.error("Сервер не ответил");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={row.resolvedAt ? "opacity-70" : undefined}>
      <CardContent className="space-y-2 py-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">№{row.id}</span>
          <Badge variant="outline">{KIND[row.kind] ?? row.kind}</Badge>
          {row.screen && <span className="text-muted-foreground">{row.screen}</span>}
          <span className="text-xs text-muted-foreground">
            {new Date(row.createdAt).toLocaleString("ru-RU", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
            {row.email ? ` · ${row.email}` : ""}
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            {row.issueUrl ? (
              <a
                href={row.issueUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
              >
                задача <ExternalLink className="size-3" />
              </a>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void resend()}>
                <Send className="size-3.5" />
                Дослать
              </Button>
            )}
          </div>
        </div>

        {row.words && <p className="text-sm">«{row.words}»</p>}
        {row.selection && (
          <p className="text-xs text-muted-foreground">Выделено: «{row.selection}»</p>
        )}
        {row.codes.length > 0 && (
          <p className="text-xs text-muted-foreground">Коды: {row.codes.join(", ")}</p>
        )}
        {row.project && <p className="text-xs text-muted-foreground">{row.project}</p>}

        {row.resolvedAt ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 className="size-3.5 text-primary" />
            Разобрано {new Date(row.resolvedAt).toLocaleDateString("ru-RU")}
            {row.resolvedBy ? ` · ${row.resolvedBy}` : ""}
            {row.resolution ? ` · ${row.resolution}` : ""}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void resolve(false)}>
              <RotateCcw className="size-3.5" />
              Вернуть в работу
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 max-w-sm text-sm"
              placeholder="Чем кончилось? (можно не писать)"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            />
            <Button size="sm" disabled={busy} onClick={() => void resolve(true)}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Flag className="size-3.5" />}
              Разобрано
            </Button>
          </div>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Свернуть" : "Экран и подробности"}
        </Button>

        {open && (
          <Details
            details={details}
            hasScreenshot={row.hasScreenshot}
            estimateId={row.estimateId}
          />
        )}
      </CardContent>
    </Card>
  );
}

function Details({
  details,
  hasScreenshot,
  estimateId,
}: {
  details: ComplaintDetails | null;
  hasScreenshot: boolean;
  estimateId: string | null;
}) {
  if (!details) return <p className="text-xs text-muted-foreground">Читаем…</p>;

  const p = details.payload;
  const text = typeof p.screenText === "string" ? p.screenText : "";
  const answer = typeof p.answer === "string" ? p.answer : "";
  const error = typeof p.screenshotError === "string" ? p.screenshotError : "";

  return (
    <div className="space-y-3 border-t pt-3">
      {estimateId && (
        <a
          href={`/estimates/${estimateId}`}
          className="inline-flex items-center gap-1 text-xs hover:underline"
        >
          открыть калькулятор <ExternalLink className="size-3" />
        </a>
      )}

      {answer && (
        <div className="text-xs">
          <p className="font-medium">Ответил</p>
          <p className="text-muted-foreground">{answer}</p>
        </div>
      )}

      {details.screenshot ? (
        // Снимок собран из разметки страницы, а не снят с экрана: для нашего
        // интерфейса это точная копия, и она единственная показывает, где что
        // наехало друг на друга.
        <img
          src={details.screenshot}
          alt="экран в момент жалобы"
          className="max-h-[70vh] w-full rounded border object-contain object-top"
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          {hasScreenshot ? "Снимок не прочитался." : `Снимка нет${error ? `: ${error}` : ""}.`}
        </p>
      )}

      {text && (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">Текст экрана</summary>
          <pre className="mt-1 max-h-72 overflow-auto rounded bg-muted p-2 text-[11px] whitespace-pre-wrap">
            {text}
          </pre>
        </details>
      )}
    </div>
  );
}
